import React, { useState, useEffect } from 'react';
import { useFrappePostCall, useFrappeGetCall, useFrappeAuth } from 'frappe-react-sdk';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, 
  Eye, 
  Calendar,
  RefreshCw,
  Sparkles,
  ChevronRight,
  BookOpen,
  FileBarChart,
  GraduationCap,
  Loader2,
  Download,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { isCurrentUserAdmin } from '../utils/api';
import { useUserRoles } from '../hooks/useUserRoles';

interface Session {
  name: string;
  title: string;
  description: string;
  session_date: string;
  status: string;
}

interface SessionReport {
  name: string;
  session: string;
  session_title?: string;
  session_date?: string;
  session_status?: string;
  report_type: string;
  report_title: string;
  report_content?: string;
  generated_by: string;
  generated_for?: string;
  generated_at: string;
}

interface ReportViewerProps {
  reportName: string;
  reportTitle: string;
  onClose: () => void;
}

const ReportViewer: React.FC<ReportViewerProps> = ({ reportName, reportTitle, onClose }) => {
  const [reportContent, setReportContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { call: getReportContent } = useFrappePostCall('surgical_training.api.session_reports.get_report_content');

  useEffect(() => {
    loadReportContent();
  }, [reportName]);

  const loadReportContent = async () => {
    try {
      setLoading(true);
      
      const response = await getReportContent({ report_name: reportName });
      
      // Check various response structures
      
      // Handle both direct and nested response structures
      const content = response?.content || response?.data?.content || response?.message?.content || response?.message?.data?.content;
      const isSuccess = response?.message === 'Success' || response?.message?.message === 'Success';
      
      if (isSuccess && content) {
        setReportContent(content);
      } else {
        toast.error('Failed to load report content');
      }
    } catch (error) {
      console.error('📄 [Report Viewer] ❌ Error loading report content:', error);
      toast.error('Failed to load report content');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([reportContent], { type: 'text/markdown' });
    const href = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = href;
    link.download = `${reportTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    
    toast.success('Report downloaded successfully!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">{reportTitle}</h2>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleDownload}
              disabled={loading || !reportContent}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Download
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              size="sm"
            >
              Close
            </Button>
          </div>
        </div>
        
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            </div>
          ) : (
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{reportContent}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SessionReportAI: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [reports, setReports] = useState<SessionReport[]>([]);
  const [viewingReport, setViewingReport] = useState<{ name: string; title: string } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [generatingReports, setGeneratingReports] = useState<{ [key: string]: 'doctor' | 'student' | null }>({});
  
  const { currentUser: authUser } = useFrappeAuth();
  const { hasDoctor } = useUserRoles();
  const isAdmin = isCurrentUserAdmin(authUser || undefined);

  // Use different API based on user role
  const shouldShowAllSessions = isAdmin || hasDoctor;
  const apiEndpoint = shouldShowAllSessions
    ? 'surgical_training.api.session.get_sessions'
    : 'surgical_training.api.session_assignment.get_user_assigned_sessions';

  const { data: sessionData, mutate: refreshSessions } = useFrappeGetCall(apiEndpoint);
  const { call: getReports } = useFrappePostCall('surgical_training.api.session_reports.get_session_reports');
  const { call: generateDoctorReportCall } = useFrappePostCall('surgical_training.api.reports.generate_doctor_summary_report');
  const { call: generateStudentReportCall } = useFrappePostCall('surgical_training.api.reports.generate_student_feedback_report');

  useEffect(() => {
    if (sessionData && typeof sessionData === 'object' && sessionData.message) {
      const responseData = sessionData.message;
      if (responseData && responseData.message === 'Success' && Array.isArray(responseData.data)) {
        setSessions(responseData.data);
      }
    }
  }, [sessionData]);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setIsRefreshing(true);
      
      const response = await getReports({ limit: 50 });
      
      // Handle both direct and nested response structures
      const reportsData = response?.data || response?.message?.data;
      const isSuccess = response?.message === 'Success' || response?.message?.message === 'Success';
      
      if (isSuccess && reportsData) {
        setReports(reportsData);
      } else {
      }
    } catch (error) {
      console.error('📋 [Load Reports] ❌ Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setIsRefreshing(false);
    }
  };


  const generateDoctorReport = async (sessionName: string) => {
    setGeneratingReports(prev => ({ ...prev, [sessionName]: 'doctor' }));

    try {
      console.log('🔵 [Doctor Report] Starting generation for session:', sessionName);

      const result = await generateDoctorReportCall({
        session_name: sessionName,
        save_to_db: true
      });

      console.log('🔵 [Doctor Report] API response:', result);

      // Check various response structures
      if (result?.message?.data?.doctor_summary || result?.data?.doctor_summary) {

        if (result?.saved_report || result?.message?.saved_report) {
          toast.success('Doctor report generated and saved! Any existing report has been updated.');
        } else {
          toast.success('Doctor report generated successfully!');
        }

        await loadReports(); // Refresh reports list
      } else {
        console.error('🔵 [Doctor Report] Unexpected response format:', result);
        toast.error('Failed to generate doctor report - unexpected response format');
      }
    } catch (error) {
      console.error('🔵 [Doctor Report] ❌ Error generating doctor report:', error);
      toast.error('Failed to generate doctor report');
    } finally {
      setGeneratingReports(prev => ({ ...prev, [sessionName]: null }));
    }
  };

  const generateStudentReport = async (sessionName: string) => {
    setGeneratingReports(prev => ({ ...prev, [sessionName]: 'student' }));

    try {
      console.log('🟢 [Student Report] Starting generation for session:', sessionName);

      const result = await generateStudentReportCall({
        session_name: sessionName,
        save_to_db: true
      });

      console.log('🟢 [Student Report] API response:', result);

      // Check various response structures
      if (result?.message?.data?.student_feedback || result?.student_feedback) {

        if (result?.saved_report || result?.message?.saved_report) {
          toast.success('Student feedback report generated and saved! Any existing report has been updated.');
        } else {
          toast.success('Student feedback report generated successfully!');
        }

        await loadReports(); // Refresh reports list
      } else {
        console.error('🟢 [Student Report] Unexpected response format:', result);
        toast.error('Failed to generate student report - unexpected response format');
      }
    } catch (error) {
      console.error('🟢 [Student Report] ❌ Error generating student report:', error);
      toast.error('Failed to generate student report');
    } finally {
      setGeneratingReports(prev => ({ ...prev, [sessionName]: null }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };



  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Group reports by session
  const reportsBySession = reports.reduce((acc, report) => {
    const sessionKey = report.session;
    if (!acc[sessionKey]) {
      acc[sessionKey] = [];
    }
    acc[sessionKey].push(report);
    return acc;
  }, {} as Record<string, SessionReport[]>);

  // Combine sessions with their reports
  const sessionsWithReports = sessions.map(session => ({
    ...session,
    reports: reportsBySession[session.name] || []
  }));

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshSessions(),
        loadReports()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const hasReportType = (sessionReports: SessionReport[], type: string) => {
    return sessionReports.some(report => report.report_type === type);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="text-yellow-500" size={20} />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Assessment Reports</h2>
            <p className="text-sm text-gray-600">
              Generate AI-powered assessment reports for training sessions
            </p>
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2"
          variant="outline"
        >
          <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>


      {/* Sessions Grid */}
      {sessionsWithReports.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="flex flex-col items-center justify-center p-12">
            <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <BookOpen size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Training Sessions Available
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              No training sessions found. Sessions will appear here when they are created and you have access to them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sessionsWithReports.map((session) => (
            <Card key={session.name} className="bg-white hover:shadow-md transition-all duration-200 border border-gray-200 hover:border-gray-300">
              <CardHeader className="pb-4 space-y-3">
                {/* Session Title and Status */}
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-900 leading-tight line-clamp-2 flex-1">
                    {session.title}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(session.status)}`}>
                    {session.status}
                  </span>
                </div>
                
                {/* Session Date */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={16} className="text-gray-400" />
                  <span>{formatDate(session.session_date)}</span>
                </div>
                
                {/* Session Description */}
                {session.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                    {session.description}
                  </p>
                )}

                {/* View Session Link */}
                <Link
                  to={`/session/${session.name}`}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-sm"
                >
                  View Session
                  <ChevronRight size={14} />
                </Link>
              </CardHeader>
              
              <CardContent className="pt-0 space-y-4">
                {/* Report Generation Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <Sparkles size={14} className="text-yellow-500" />
                    Generate AI Reports
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => generateDoctorReport(session.name)}
                      disabled={generatingReports[session.name] === 'doctor'}
                      size="sm"
                      className="h-9 bg-blue-600 hover:bg-blue-700 text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all"
                    >
                      {generatingReports[session.name] === 'doctor' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : hasReportType(session.reports, 'Doctor Summary') ? (
                        <CheckCircle size={14} />
                      ) : (
                        <FileBarChart size={14} />
                      )}
                      <span className="text-xs font-medium ml-1">
                        {generatingReports[session.name] === 'doctor' ? 'Generating...' : 'Doctor'}
                      </span>
                    </Button>
                    
                    <Button
                      onClick={() => generateStudentReport(session.name)}
                      disabled={generatingReports[session.name] === 'student'}
                      size="sm"
                      className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-sm focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-all"
                    >
                      {generatingReports[session.name] === 'student' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : hasReportType(session.reports, 'Student Feedback') ? (
                        <CheckCircle size={14} />
                      ) : (
                        <GraduationCap size={14} />
                      )}
                      <span className="text-xs font-medium ml-1">
                        {generatingReports[session.name] === 'student' ? 'Generating...' : 'Student'}
                      </span>
                    </Button>
                  </div>
                </div>

                {/* View Reports Section */}
                {session.reports.length > 0 && (
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <FileText size={14} className="text-gray-500" />
                      View Saved Reports
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Doctor Report View Button */}
                      {(() => {
                        const doctorReport = session.reports.find(r => r.report_type === 'Doctor Summary');
                        return doctorReport ? (
                          <Button
                            onClick={() => setViewingReport({ name: doctorReport.name, title: doctorReport.report_title })}
                            variant="outline"
                            size="sm"
                            className="h-9 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all"
                          >
                            <Eye size={14} />
                            <span className="text-xs font-medium ml-1">View Doctor</span>
                          </Button>
                        ) : (
                          <div className="h-9 border border-dashed border-gray-200 rounded-md flex items-center justify-center">
                            <span className="text-xs text-gray-400">No Doctor Report</span>
                          </div>
                        );
                      })()}
                      
                      {/* Student Report View Button */}
                      {(() => {
                        const studentReport = session.reports.find(r => r.report_type === 'Student Feedback');
                        return studentReport ? (
                          <Button
                            onClick={() => setViewingReport({ name: studentReport.name, title: studentReport.report_title })}
                            variant="outline"
                            size="sm"
                            className="h-9 border-green-200 text-green-700 hover:bg-green-50 hover:border-green-300 focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-all"
                          >
                            <Eye size={14} />
                            <span className="text-xs font-medium ml-1">View Student</span>
                          </Button>
                        ) : (
                          <div className="h-9 border border-dashed border-gray-200 rounded-md flex items-center justify-center">
                            <span className="text-xs text-gray-400">No Student Report</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Report Viewer Modal */}
      {viewingReport && (
        <ReportViewer
          reportName={viewingReport.name}
          reportTitle={viewingReport.title}
          onClose={() => setViewingReport(null)}
        />
      )}
    </div>
  );
};

export default SessionReportAI;